import { forEach } from 'lodash';
import { DataTexture, FloatType, RGBAFormat } from 'three';
import { getCalibrationDataForFrame } from '../../cross-team-shared/misc-utils/projection-utils';
import { Point3D } from '../../team-apis/otterdam/answers/shape-typings';
import { getVerticesFromLocation } from '../render/mesh/cloud/point-cloud-utils';
import { CuboidLocationState3D } from '../state/cuboid-location-state-3d';
import { CuboidState3D } from '../state/cuboid-state-3d';
import { PointCloudContentState } from '../state/point-cloud-content-state';
import { getLocationTimestamp } from '../task-data/sensor-location';
import {
  getEgoMotionCompensationTexture,
  KeyLocationVelocity,
  KeyLocationWithTime,
} from './materials/ego-motion-compensation-material-utils';
import { interpolateLocation } from './point-cloud-interpolation-helper';

const emptyOffsetTexture = new DataTexture(
  new Float32Array(20),
  5,
  1,
  RGBAFormat,
  FloatType,
);

export function getMaterialParams(
  state: PointCloudContentState,
  outputName: string,
) {
  if (!state.calibrationData.hasOwnProperty(outputName)) {
    return {
      compensatedCuboids: emptyOffsetTexture,
      compensatedCuboidCount: 0,
    };
  }

  const velocities = getCuboidVelocity(state, outputName);

  return {
    compensatedCuboids: getEgoMotionCompensationTexture(velocities, outputName),
    compensatedCuboidCount: velocities.length,
  };
}

function getCurrentCameraTimestamp(
  state: PointCloudContentState,
  outputName: string,
): number {
  const calibration = state.calibrationData[outputName];
  if (!state.calibrationData.hasOwnProperty(outputName)) {
    return 0;
  }

  const cameraEpoch =
    getCalibrationDataForFrame(state.render.frameNumber, calibration)
      .timestamp || 0;

  return cameraEpoch;
}

function getCuboidVelocity(state: PointCloudContentState, outputName: string) {
  const cuboids = state.render.cuboids;
  const frameNumber = state.render.frameNumber;
  const motionCompensatedCuboids: KeyLocationVelocity[] = [];

  for (const cuboid of Object.values(cuboids)) {
    const location = interpolateLocation(cuboid.key_locations, frameNumber);
    if (location.visibility > 0) {
      const offset = computeCurrentFrameLocation(
        state,
        cuboid,
        location,
        outputName,
      );
      motionCompensatedCuboids.push({
        cuboid: getVerticesFromLocation(location),
        offset,
      });
    }
  }

  return motionCompensatedCuboids;
}

export function computeCuboidOffset(
  state: PointCloudContentState,
  outputName: string,
  index: number,
) {
  let offset: Point3D = [0, 0, 0];
  if (!state.calibrationData.hasOwnProperty(outputName)) {
    return offset;
  }

  const frameNumber = state.render.frameNumber;
  const cuboid = state.render.cuboids[index];
  const location = interpolateLocation(cuboid.key_locations, frameNumber);
  if (location.visibility > 0) {
    offset = computeCurrentFrameLocation(state, cuboid, location, outputName);
  }

  return offset;
}

function computeCurrentFrameLocation(
  state: PointCloudContentState,
  cuboid: CuboidState3D,
  location: CuboidLocationState3D,
  outputName: string,
): Point3D {
  const cameraEpoch = getCurrentCameraTimestamp(state, outputName);
  if (cameraEpoch <= 0) {
    throw new Error(`Missing timestamp for camera ${outputName}`);
  }
  const previousKeyLocationIndex = getPreviousKeyLocationIndex(
    cuboid,
    location,
  );
  const previousKeyLocation = makeKeyLocationWithTime(
    state,
    cuboid,
    previousKeyLocationIndex,
  );
  const nextKeyLocation = makeKeyLocationWithTime(
    state,
    cuboid,
    previousKeyLocationIndex + 1,
  );

  if (!previousKeyLocation || !nextKeyLocation) {
    return [0, 0, 0];
  }

  // Only consider the offset over the interpolated cuboid
  const locationWithTime = makeKeyLocationWithTimeFromCuboid(state, location);
  return interpolateOffset(locationWithTime, nextKeyLocation, cameraEpoch);
}

function makeKeyLocationWithTimeFromCuboid(
  state: PointCloudContentState,
  location: CuboidLocationState3D,
) {
  return {
    center: location.position,
    timestamp: getLocationTimestamp(state.locations, location.frame_number),
    vertices: getVerticesFromLocation(location),
  };
}

function makeKeyLocationWithTime(
  state: PointCloudContentState,
  cuboid: CuboidState3D,
  keyLocationIndex: number,
): KeyLocationWithTime | undefined {
  if (keyLocationIndex < 0 || keyLocationIndex >= cuboid.key_locations.length) {
    return undefined;
  }

  const keyLocation = cuboid.key_locations[keyLocationIndex];
  const timestamp = getLocationTimestamp(
    state.locations,
    keyLocation.frame_number,
  );
  if (timestamp <= 0) {
    throw new Error(`Missing timestamp for key location ${keyLocationIndex}`);
  }

  return {
    vertices: getVerticesFromLocation(keyLocation),
    center: keyLocation.position,
    timestamp,
  };
}

function getPreviousKeyLocationIndex(
  cuboid: CuboidState3D,
  interpolatedLocation: CuboidLocationState3D,
) {
  let previousKeyLocationIndex = -1;
  forEach(cuboid.key_locations, (keyLocation, index) => {
    if (keyLocation.frame_number < interpolatedLocation.frame_number) {
      previousKeyLocationIndex = index;
    }
  });

  return previousKeyLocationIndex;
}

function interpolateOffset(
  cuboid: KeyLocationWithTime,
  otherCuboid: KeyLocationWithTime,
  timestamp: number,
): Point3D {
  let t1: number = cuboid.timestamp;
  let [x1, y1, z1] = cuboid.center;
  let t2: number = otherCuboid.timestamp;
  let [x2, y2, z2] = otherCuboid.center;

  if (cuboid.timestamp > otherCuboid.timestamp) {
    t1 = otherCuboid.timestamp;
    [x1, y1, z1] = otherCuboid.center;
    t2 = cuboid.timestamp;
    [x2, y2, z2] = cuboid.center;
  }

  const offsetX = computeOffset([t1, x1], [t2, x2], timestamp);
  const offsetY = computeOffset([t1, y1], [t2, y2], timestamp);
  const offsetZ = computeOffset([t1, z1], [t2, z2], timestamp);

  return [offsetX, offsetY, offsetZ];
}

function computeOffset(
  [t1, p1]: [number, number],
  [t2, p2]: [number, number],
  t: number,
) {
  const timeSpan = t2 - t1;
  if (timeSpan === 0) {
    return 0;
  }

  return ((p2 - p1) / (t2 - t1)) * (t - t1);
}
